import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthCard } from "@/components/AuthCard";
import { PasswordFields } from "@/components/PasswordFields";
import { Button } from "@/components/ui/button";
import { Loader2, XCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const token = new URLSearchParams(useLocation().search).get("token") || "";
  const [check, setCheck] = useState(null); // null checking, {valid,email_hint} or {error}
  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setCheck({ error: "This reset link is missing its code. Please use the link from your email." }); return; }
    api.get(`/auth/reset-password/${encodeURIComponent(token)}`).then((r) => setCheck(r.data)).catch((e) => setCheck({ error: apiErr(e) }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.password.length < 8) return toast.error("Use at least 8 characters");
    if (pw.password !== pw.confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw.password });
      await refresh();
      toast.success("Password updated — you're signed in");
      navigate(data.role === "landlord" ? "/dashboard" : "/portal", { replace: true });
    } catch (err) { toast.error(apiErr(err)); setLoading(false); }
  };

  const back = <Link to="/login" className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline" data-testid="reset-back-login"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>;

  if (!check) return <div className="min-h-screen flex items-center justify-center text-slate-500 gap-2"><Loader2 className="h-6 w-6 animate-spin text-primary" /> Checking your link…</div>;

  if (check.error) {
    return (
      <AuthCard title="Link expired or invalid" subtitle={check.error} testid="reset-invalid"
        footer={back}>
        <div className="rounded-xl bg-rose-50 border border-rose-100 p-5 text-center">
          <XCircle className="h-10 w-10 text-rose-500 mx-auto mb-3" />
          <Button asChild className="gap-2" data-testid="reset-request-new"><Link to="/forgot-password">Request a new link</Link></Button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Choose a new password" subtitle={<>Setting a new password for <b className="text-slate-800" data-testid="reset-email-hint">{check.email_hint}</b>. You'll be signed in right after.</>} footer={back}>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <PasswordFields value={pw} onChange={setPw} prefix="reset" />
        <Button type="submit" disabled={loading} className="w-full h-11 text-sm font-semibold gap-2" data-testid="reset-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password & sign in"}
        </Button>
      </form>
    </AuthCard>
  );
}
